/**
 * Record (or correct) the actual / invoice of an approved expense (migration 976).
 *
 * The server keeps the approved estimate, computes the variance, derives the due date from the vendor's payment
 * terms on the real dates (a different date needs a reason), flags payee / department / GL / terms changes, and
 * decides the route: within the auto-approval limit -> Ready for Export; above it, or any corrected resubmission ->
 * the approver. An invoice or receipt document must be attached first (setting). Only finance may override the GL.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../common/Modal";
import Button from "../common/Button";
import {
  actualizeExpense,
  financeErrorMessage,
  getFinanceLookups,
  money,
  payeeLabel,
  searchPayeeOptions,
} from "../../services/finance.service";
import SearchableSelect from "../common/SearchableSelect";
import type { ActualFields, Expense } from "../../types/finance.types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (e: Expense) => void;
  expense: Expense;
  isFinanceAdmin: boolean;
}

const inputCls =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";
const labelCls = "block text-sm font-medium text-gray-700";

export default function ActualFormModal({
  isOpen,
  onClose,
  onSaved,
  expense,
  isFinanceAdmin,
}: Props) {
  const { data: lk } = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    enabled: isOpen,
    staleTime: 60_000,
  });
  const [amount, setAmount] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueReason, setDueReason] = useState("");
  const [notes, setNotes] = useState("");
  const [payeeId, setPayeeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [gl, setGl] = useState("");
  const [glReason, setGlReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError(null);
    setAmount(
      expense.actual_amount !== null && expense.actual_amount !== undefined
        ? String(expense.actual_amount)
        : "",
    );
    setServiceDate(
      expense.actual_service_date ?? expense.anticipated_service_date ?? "",
    );
    setInvoiceDate(expense.invoice_date ?? "");
    setInvoiceNo(expense.invoice_number ?? "");
    setDueDate(
      expense.actual_due_overridden ? (expense.actual_due_date ?? "") : "",
    );
    setDueReason(expense.actual_due_override_reason ?? "");
    setNotes(expense.actual_notes ?? "");
    setPayeeId(expense.payee_id);
    setDepartmentId(expense.department_id);
    setCategoryId(expense.billing_category_id);
    setGl("");
    setGlReason("");
  }, [isOpen, expense]);

  const n = Number(amount);
  const variance =
    amount &&
    expense.approved_amount !== null &&
    expense.approved_amount !== undefined
      ? n - Number(expense.approved_amount)
      : null;

  const save = async () => {
    setError(null);
    if (!amount || !serviceDate || !invoiceDate || !invoiceNo.trim()) {
      setError(
        "Fill in the actual amount, service date, invoice date and invoice number.",
      );
      return;
    }
    if (dueDate && !dueReason.trim()) {
      setError(
        "Give a reason for setting the due date yourself (leave it empty to use the vendor's payment terms).",
      );
      return;
    }
    const f: ActualFields = {
      actual_amount: n,
      actual_service_date: serviceDate,
      invoice_date: invoiceDate,
      invoice_number: invoiceNo.trim(),
      actual_due_date: dueDate || null,
      actual_due_override_reason: dueDate ? dueReason.trim() : null,
      actual_notes: notes.trim() || null,
    };
    if (payeeId !== expense.payee_id) {
      f.payee_id = payeeId;
    }
    if (departmentId !== expense.department_id) {
      f.department_id = departmentId;
    }
    if (categoryId !== expense.billing_category_id) {
      f.billing_category_id = categoryId;
    }
    if (isFinanceAdmin && gl.trim()) {
      f.gl_account_no = gl.trim();
      f.gl_override_reason = glReason.trim() || null;
    }
    setSaving(true);
    try {
      onSaved(await actualizeExpense(expense, f));
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Actual / invoice for ${expense.expense_no}`}
      size="lg"
    >
      <div className="space-y-4" data-testid="finance-actual-form">
        <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
          Approved estimate: <strong>{money(expense.approved_amount)}</strong>.
          An actual within the auto-approval limit goes straight to Ready for
          Export; a larger one (or any correction after a return) goes back to
          the approver. Attach the invoice in the Documents section first.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="fa-amount">
              Actual amount (USD)
            </label>
            <input
              id="fa-amount"
              type="number"
              min="0.01"
              step="0.01"
              className={inputCls}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {variance !== null && (
              <p
                className={`mt-1 text-xs ${variance > 0 ? "text-red-700" : "text-gray-500"}`}
              >
                Variance {money(variance)}
                {expense.approved_amount
                  ? ` (${((variance / Number(expense.approved_amount)) * 100).toFixed(1)}%)`
                  : ""}
              </p>
            )}
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-inv">
              Vendor invoice number
            </label>
            <input
              id="fa-inv"
              className={inputCls}
              maxLength={60}
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-idate">
              Invoice date
            </label>
            <input
              id="fa-idate"
              type="date"
              className={inputCls}
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-sdate">
              Actual service date
            </label>
            <input
              id="fa-sdate"
              type="date"
              className={inputCls}
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-due">
              Due date (optional)
            </label>
            <input
              id="fa-due"
              type="date"
              className={inputCls}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to use the vendor&apos;s payment terms.
            </p>
          </div>
          {dueDate && (
            <div>
              <label className={labelCls} htmlFor="fa-due-reason">
                Why this due date?
              </label>
              <input
                id="fa-due-reason"
                className={inputCls}
                value={dueReason}
                onChange={(e) => setDueReason(e.target.value)}
              />
            </div>
          )}
          <div>
            {/* Server-side type-to-search; the approved vendor stays shown (labelled) until changed. */}
            <SearchableSelect
              label="Vendor on the invoice"
              value={payeeId}
              onChange={setPayeeId}
              search={searchPayeeOptions}
              resolveLabel={payeeLabel}
              queryKeyBase="finance-payee-picker-actual"
              placeholder="Search by vendor name or vendor number..."
              testId="fa-payee"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-dept">
              Department
            </label>
            <select
              id="fa-dept"
              className={inputCls}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              {(lk?.departments ?? [])
                .filter((d) => d.is_active || d.id === expense.department_id)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="fa-cat">
              Billing category
            </label>
            <select
              id="fa-cat"
              className={inputCls}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              {(lk?.categories ?? [])
                .filter(
                  (c) => c.is_active || c.id === expense.billing_category_id,
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          {isFinanceAdmin && (
            <div>
              <label className={labelCls} htmlFor="fa-gl">
                Override GL account (finance only)
              </label>
              <input
                id="fa-gl"
                className={inputCls}
                value={gl}
                onChange={(e) => setGl(e.target.value)}
              />
              {gl && (
                <input
                  aria-label="GL override reason"
                  placeholder="Reason"
                  className={inputCls}
                  value={glReason}
                  onChange={(e) => setGlReason(e.target.value)}
                />
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="fa-notes">
              Notes
            </label>
            <textarea
              id="fa-notes"
              rows={2}
              maxLength={2000}
              className={inputCls}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save actual"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
